/**
 * The React boundary — and it is deliberately as thin as a boundary can be.
 *
 * ARCHITECTURE.md §4.7: ticks never touch React state. These hooks read from
 * typed arrays through `useSyncExternalStore`, which is the API that exists
 * for exactly this and is tear-free: with `useState` a concurrent render can
 * read one slot before an update and the next slot after it, and a chain
 * showing two different instants is a chain showing a spread that never
 * existed.
 */

import { useCallback, useEffect, useMemo, useSyncExternalStore } from 'react'
import { stalenessOf, type Staleness, type TickField } from '@apna/shared'
import { TICK_SOURCE, tickStore, type TickSource } from './tickStore'
import { tickSocket, type TickSocketState } from './tickSocket'

/**
 * Subscribe to tokens for as long as the component is mounted.
 *
 * Refcounted in `tickSocket`, so two components wanting the same strike cost
 * one server-side subscription and unmounting one does not blind the other.
 */
export function useTickSubscription(tokens: readonly number[]): void {
  // Keyed on the CONTENTS, not the array identity: a caller that builds the
  // list inline would otherwise resubscribe on every render.
  const key = tokens.join(',')
  useEffect(() => {
    if (tokens.length === 0) return
    const release = tickSocket.subscribe([...tokens])
    return release
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key])
}

/**
 * Declare the chain this screen is reading, for OPEN INTEREST.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * CLEARED ON UNMOUNT, DELIBERATELY
 *
 * Open interest is the expensive half of a subscription — a second one per
 * contract at the broker ([B2]) — and there is room for roughly one chain's
 * worth. A screen that navigates away is no longer reading anything, so it
 * gives the foreground back rather than holding it until the socket closes.
 *
 * Coming back is cheap: the pool holds a surplus code for the same grace
 * period it holds an unheld token (§4.8b), so returning within a minute costs
 * nothing at the broker.
 * ─────────────────────────────────────────────────────────────────────────────
 */
export function useForegroundChain(tokens: readonly number[]): void {
  // Keyed on CONTENTS, as the subscription hooks are: a caller that builds
  // the list inline would otherwise redeclare on every render.
  const key = tokens.join(',')
  useEffect(() => {
    tickSocket.setForeground([...tokens])
    return () => tickSocket.setForeground([])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key])
}

/**
 * One field of one instrument.
 *
 * This is what a chain cell calls. It re-renders when that instrument ticks
 * and at no other time — not when a sibling cell ticks, not when the socket
 * reconnects, not when the chain changes.
 */
export function useTickField(token: number, field: TickField): number {
  const slot = useMemo(() => tickStore.track(token), [token])

  const subscribe = useCallback(
    (onChange: () => void) => tickStore.subscribeSlot(slot, onChange),
    [slot],
  )
  // The VERSION is the snapshot, not the value: returning the value breaks on
  // a tick that does not change it, which is common on an illiquid strike
  // where open interest moves and the price does not.
  const version = useSyncExternalStore(subscribe, () => tickStore.versionOf(slot))
  void version
  return tickStore.field(slot, field)
}

/**
 * One field AND the instrument's exchange stamp, from a SINGLE subscription.
 *
 * What a chain cell needs: the number to show, and the timestamp that decides
 * whether to dim it. Two calls to `useTickField` would give the same answer
 * and set up two `useSyncExternalStore` subscriptions to the same slot — and
 * React then commits the component TWICE per tick, which the 3.14 render
 * profile measured as 460 renders for 230 records.
 *
 * One subscription, two reads.
 */
export function useTickCell(
  token: number,
  field: TickField,
): { value: number; exchTs: number; source: TickSource } {
  const slot = useMemo(() => tickStore.track(token), [token])
  const subscribe = useCallback(
    (onChange: () => void) => tickStore.subscribeSlot(slot, onChange),
    [slot],
  )
  useSyncExternalStore(subscribe, () => tickStore.versionOf(slot))
  return {
    value: tickStore.field(slot, field),
    exchTs: tickStore.field(slot, 'exchTs'),
    source: tickStore.sourceOf(slot),
  }
}

/**
 * `ltp` for MANY instruments, from ONE subscription — ROADMAP 6.7.
 *
 * What a basket total needs. The obvious alternative is to call `useTickCell`
 * once per leg in a loop, which is a React rule violation and not a
 * theoretical one: a group that gains or loses a leg between two position
 * polls renders a DIFFERENT NUMBER OF HOOKS than the render before it, and
 * React throws rather than reconciling. A strategy whose leg is squared off
 * is exactly that case.
 *
 * So the arity is fixed at one hook whatever the list length, and the
 * subscription covers every slot in it.
 *
 * Returns a Map keyed by token. Absent means "no tick for this instrument",
 * which the caller must keep distinct from zero — a basket that reads an
 * unmarked leg as flat understates the risk it is describing.
 */
export function useTickLtps(tokens: readonly number[]): Map<number, number> {
  // Keyed on the CONTENTS, as `useTickSubscription` is: a caller that builds
  // the list inline would otherwise re-track on every render.
  const key = tokens.join(',')
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const slots = useMemo(() => tokens.map((t) => tickStore.track(t)), [key])

  const subscribe = useCallback(
    (onChange: () => void) => {
      const releases = slots.map((slot) => tickStore.subscribeSlot(slot, onChange))
      return () => {
        for (const release of releases) release()
      }
    },
    [slots],
  )

  /*
   * The SUM of versions is the snapshot. Versions only ever increase, so the
   * sum changes whenever any slot in the set ticks and can never collide with
   * a previous state — which a hash of the values could.
   */
  useSyncExternalStore(subscribe, () =>
    slots.reduce((sum, slot) => sum + tickStore.versionOf(slot), 0),
  )

  const out = new Map<number, number>()
  slots.forEach((slot, i) => {
    const ltp = tickStore.field(slot, 'ltp')
    // Zero is the store's "nothing yet", not a price. Left ABSENT so the
    // caller cannot read it as a mark.
    if (ltp > 0) out.set(tokens[i]!, ltp)
  })
  return out
}

export interface TickView {
  ltp: number
  exchTs: number
  staleness: Staleness
  ageMs: number
  /** False until the first frame for this instrument has landed. */
  hasData: boolean
  /** A REST snapshot that has not been upgraded to live yet (§4.8a). */
  isSnapshot: boolean
  /** The last close of a finished session. Only ever set outside market hours. */
  isClose: boolean
}

/**
 * Price plus freshness for one instrument.
 *
 * Freshness is per CELL, from that instrument's own `exchTs` (§4.7). With a
 * shared pool at the ceiling an eviction freezes a price someone else is
 * looking at, and the chain as a whole looks perfectly healthy — per-cell
 * staleness is the only honest defence.
 */
export function useTick(token: number): TickView {
  const slot = useMemo(() => tickStore.track(token), [token])
  const subscribe = useCallback(
    (onChange: () => void) => tickStore.subscribeSlot(slot, onChange),
    [slot],
  )
  const version = useSyncExternalStore(subscribe, () => tickStore.versionOf(slot))

  const exchTs = tickStore.field(slot, 'exchTs')
  const now = Date.now()
  return {
    ltp: tickStore.field(slot, 'ltp'),
    exchTs,
    staleness: exchTs > 0 ? stalenessOf(exchTs, now) : 'DEAD',
    ageMs: exchTs > 0 ? now - exchTs : 0,
    hasData: version > 0,
    isSnapshot: tickStore.sourceOf(slot) === TICK_SOURCE.SNAPSHOT,
    isClose: tickStore.sourceOf(slot) === TICK_SOURCE.CLOSE,
  }
}

/** The socket's connection state, for a status indicator. */
export function useTickSocketState(): TickSocketState {
  return useSyncExternalStore(
    (onChange) => tickSocket.onState(() => onChange()),
    () => tickSocket.getState(),
    () => 'IDLE' as const,
  )
}

/**
 * Store and socket counters.
 *
 * Subscribed to the GLOBAL listener, so it updates on any frame — which is
 * correct for a diagnostics panel and would be wrong for a cell. Nothing that
 * renders per instrument should use this.
 */
export function useFeedDiagnostics() {
  const version = useSyncExternalStore(
    (onChange) => tickStore.subscribeAll(onChange),
    () => tickStore.stats().framesApplied,
    () => 0,
  )
  void version
  return { store: tickStore.stats(), socket: tickSocket.getStats() }
}
